import {
  AlertCircleIcon,
  Loading03Icon,
  NotificationSquareIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { DashboardBanner } from "../DashboardBanner";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { getFullName } from "shared/utils/get-full-name";
import { Application } from "types/application";
import { DateTime } from "luxon";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { applicationsApi } from "api/applications";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

const ViewDocumentsAction = ({
  applicantId,
  applicationId
}: {
  applicantId: string;
  applicationId: string;
}) => {
  const navigate = useNavigate();

  const goToDocuments = () =>
    navigate(
      `/applications/${applicationId}/reports?applicantId=${applicantId}&report=additional_documents`
    );

  const markAsSeen = useMutation({
    mutationKey: ["APPLICATION"],
    mutationFn: () =>
      applicationsApi.markDocumentRequestsSeen({ applicantId, applicationId }),
    // in both examples we want to navigate user
    onSuccess: goToDocuments,
    onError: goToDocuments
  });

  return (
    <Button
      size={ButtonSize.XS}
      loading={markAsSeen.isPending}
      onClick={() => markAsSeen.mutate()}
      variant={ButtonVariant.PRIMARY}
    >
      View documents
    </Button>
  );
};

const CancelRequestAction = ({
  applicantId,
  applicationId,
  documentRequestId
}: {
  applicantId: string;
  applicationId: string;
  documentRequestId: number;
}) => {
  const [confirming, setConfirming] = useState(false);
  const queryClient = useQueryClient();

  const cancelRequest = useMutation({
    mutationKey: ["APPLICATION", "CANCEL_DOCUMENT_REQUEST"],
    mutationFn: () =>
      applicationsApi.cancelDocumentRequest({
        applicantId,
        applicationId,
        documentRequestId
      }),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      toast.success("Request canceled successfully");
      setConfirming(false);
    }
  });

  return (
    <>
      <Button size={ButtonSize.XS} onClick={() => setConfirming(true)}>
        Cancel request
      </Button>

      <ConfirmDialog
        opened={confirming}
        title="Cancel this request?"
        description="The applicant will be notified. You can send a new request once this one's canceled."
        variant="warning"
        icon={{ icon: AlertCircleIcon, variant: "warning" }}
        confirmButton={{
          label: "Cancel request",
          loading: cancelRequest.isPending
        }}
        hideCancelButton
        showCloseButton
        onClose={() => {
          if (cancelRequest.isPending) return;
          setConfirming(false);
        }}
        onConfirm={() => cancelRequest.mutate()}
      />
    </>
  );
};

export const UploadedDocumentsBanners = ({
  application
}: {
  application: Application;
}) => {
  const banners = application.applicants
    .map((applicant) => {
      const documentRequests = applicant.document_requests || [];
      const pendingDocRequests = documentRequests.filter(
        (dr) => dr.is_uploaded === false
      );
      const notSeenRequests = documentRequests.filter(
        (dr) => dr.seen_by_partner_user === false
      );

      const applicantName = getFullName({
        firstName: applicant.first_name,
        lastName: applicant.last_name,
        middleName: applicant.middle_name,
        short: true
      });

      if (pendingDocRequests.length)
        return (
          <DashboardBanner
            key={applicant.id}
            title="Waiting for additional documents"
            body={`Additional document request was sent to ${applicantName}${
              pendingDocRequests[0].requested_by_name
                ? ` by ${pendingDocRequests[0].requested_by_name}`
                : ""
            } on ${DateTime.fromISO(pendingDocRequests[0].created_at).toLocaleString(DateTime.DATE_MED)}`}
            icon={Loading03Icon}
            action={
              <CancelRequestAction
                applicantId={String(applicant.id)}
                applicationId={String(applicant.application_id)}
                documentRequestId={pendingDocRequests[0].id}
              />
            }
          />
        );

      if (notSeenRequests.length)
        return (
          <DashboardBanner
            key={applicant.id}
            title="New documents received"
            body={`${applicantName} has sent additional documents that may be relevant to your decision`}
            icon={NotificationSquareIcon}
            action={
              <ViewDocumentsAction
                applicantId={String(applicant.id)}
                applicationId={String(applicant.application_id)}
              />
            }
          />
        );

      return null;
    })
    .filter(Boolean);

  if (!banners.length) return null;

  return <div className="space-y-3">{banners}</div>;
};
